import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Plus, Clock, CheckCircle, XCircle, FileEdit} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {AutoAssignRuleRequestControllerRequestsEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {RuleRequestStatus, RuleRequestType} from '@/types/auto-assign-rule-request'
import {BadgeVariant} from '@/components/ui/badge'
import {formatDateOnly} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'

export function AutoAssignRuleRequests() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.autoAssignRuleRequests.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        sortBy, sortDir,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: AutoAssignRuleRequestControllerRequestsEndpoint,
        sortable: true,
        defaultSortBy: AutoAssignRuleRequestControllerRequestsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const canCreate = hasPermission(permissions.autoAssignRules.create)

    // Issue 342.05 / closes 244 — empty URL shows ALL requests. The previous
    // implementation defaulted to {DRAFT, PENDING} and silently re-applied that
    // selection whenever the user cleared the chips, which made approved/
    // rejected/cancelled rows invisible without an explicit URL override.
    // The unified bar carries every active filter as a chip; nothing implicit.
    const {data: requestsData} = useQuery({
        queryKey: ['rule-requests', queryString, page, pageSize, sortBy, sortDir],
        queryFn: () => services.autoAssignRules.listRequests(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const requests = requestsData?.content || []
    const totalItems = requestsData?.totalElements || 0
    const totalPages = requestsData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    // Get status badge variant and icon
    const getStatusBadge = (requestStatus: RuleRequestStatus) => {
        switch (requestStatus) {
            case RuleRequestStatus.DRAFT:
                return {variant: BadgeVariant.SECONDARY, icon: FileEdit}
            case RuleRequestStatus.PENDING:
                return {variant: BadgeVariant.WARNING, icon: Clock}
            case RuleRequestStatus.APPROVED:
                return {variant: BadgeVariant.SUCCESS, icon: CheckCircle}
            case RuleRequestStatus.REJECTED:
                return {variant: BadgeVariant.DESTRUCTIVE, icon: XCircle}
            default:
                return {variant: BadgeVariant.OUTLINE, icon: FileEdit}
        }
    }

    // Get request type label
    const getRequestTypeLabel = (requestType: RuleRequestType) => {
        switch (requestType) {
            case RuleRequestType.CREATE:
                return i18n.autoAssignRuleRequests.newRule
            case RuleRequestType.UPDATE:
                return i18n.autoAssignRuleRequests.modifyRule
            case RuleRequestType.ACTIVATE:
                return i18n.autoAssignRuleRequests.activateRule
            case RuleRequestType.DEACTIVATE:
                return i18n.autoAssignRuleRequests.deactivateRule
        }
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.autoAssignRuleRequest}
            title={i18n.autoAssignRuleRequests.allRequests}
            count={totalItems}
            description={`${i18n.autoAssignRuleRequests.requestCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="auto-assign-rule-requests-list"
            actions={canCreate ? (
                <Button onClick={() => navigate(routes.accessGovernance.autoAssignRuleRequests.create())}
                        data-testid="btn-create-request">
                    <Icon icon={Plus} size="md" className="mr-2"/>
                    {i18n.autoAssignRuleRequests.createRequest}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={AutoAssignRuleRequestControllerRequestsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.autoAssignRuleRequests.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-request-type">{i18n.autoAssignRuleRequests.requestType}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-owner">{i18n.autoAssignRules.owner}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {requests.map((request) => {
                        const statusBadge = getStatusBadge(request.status)
                        const StatusIcon = statusBadge.icon
                        return (
                            <ClickableTableRow key={request.id}
                                      to={routes.accessGovernance.autoAssignRuleRequests.detail(request.id)}>
                                <TableCell className="font-medium">
                                    <TextLink
                                        to={routes.accessGovernance.autoAssignRuleRequests.detail(request.id)}
                                        stopPropagation
                                        testId={`link-request-detail-${request.id}`}
                                    >
                                        {request.name}
                                    </TextLink>
                                    {request.description && (
                                        <MutedText size="sm" block className="truncate max-w-xs">
                                            {request.description}
                                        </MutedText>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {getRequestTypeLabel(request.requestType)}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <Badge variant={statusBadge.variant}
                                           className="gap-1 w-fit">
                                        <Icon icon={StatusIcon} size="xs"/>
                                        {request.status}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {request.owners.map(o => o.ownerPartyDisplayName).join(', ') || '-'}
                                </TableCell>
                                <TableCell>
                                    {formatDateOnly(request.createdAt)}
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {requests.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.autoAssignRuleRequests.noRequestsMatch}
                            emptyMessage={i18n.autoAssignRuleRequests.noRequestsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
